import React, { useState, useEffect } from "react";
import {
  VStack,
  HStack,
  Text,
  Textarea,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { FaMagic, FaChevronDown } from "react-icons/fa";
import { Header } from "build-ts/metadata";
import { PlanInput } from "build-ts/agent";
import { enrichPlan } from "services/AnalysisPlanServices";
import showToast from "hooks/useCustomToast";
import { useAnalysisPlanContext, buildKGAnalysisPlanProto } from "context/batch/AnalysisPlanContext";

interface PlanDescriptionComponentProps {
  currentPlanHeader?: Header;
  onSave: (description: string) => Promise<void>;
  isLoading?: boolean;
  isExecutingPlan?: boolean;
  isFetchingPlan?: boolean;
}

const PlanDescriptionComponent: React.FC<PlanDescriptionComponentProps> = ({
  currentPlanHeader,
  onSave,
  isLoading = false,
  isExecutingPlan = false,
  isFetchingPlan = false,
}) => {
  const [editingDescription, setEditingDescription] = useState<string>("");
  const [enrichInstruction, setEnrichInstruction] = useState<string>("");
  const [isEnrichingPlan, setIsEnrichingPlan] = useState<boolean>(false);

  const { plan, nodeMap, selectedMetricHeader, currentPlanHeader: currentPlanHeaderFromContext } = useAnalysisPlanContext();

  // suggested description
  const [suggestedDescription, setSuggestedDescription] = useState<string>("");

  // Enrich plan instruction modal disclosure
  const { 
    isOpen: isEnrichInstructionModalOpen, 
    onOpen: onEnrichInstructionModalOpen, 
    onClose: onEnrichInstructionModalClose 
  } = useDisclosure();

  // Initialize description when component mounts or currentPlanHeader changes
  useEffect(() => {
      setEditingDescription(currentPlanHeader?.description || "");
  }, [currentPlanHeader?.description]);

  // Handle description save
  const handleDescriptionSave = async () => {
    try {
      await onSave(editingDescription?.trim() || "");
    } catch (error) {
      console.error("Error saving description:", error);
      showToast("Failed to save description", "", "error");
    }
  };

  // Handle description key down
  const handleDescriptionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleDescriptionSave();
    } else if (e.key === "Escape") {
      setEditingDescription(currentPlanHeader?.description || "");
    }
  };

  // Handle Enrich Plan
  const handleEnrichPlan = async (instruction: string | "") => {
    if (!currentPlanHeader?.id || !plan) {
      showToast("No plan selected to enrich", "", "error");
      return;
    }

    setIsEnrichingPlan(true);
    try {
      if(plan){
        const currentPlanProto = buildKGAnalysisPlanProto(plan, nodeMap, selectedMetricHeader, currentPlanHeaderFromContext);
        const planInput = PlanInput.create({
          plan: currentPlanProto,
        });
        const enrichedPlan = await enrichPlan(planInput, instruction);
        if (enrichedPlan) {
          if(enrichedPlan[0]?.messages){
            for(let i=enrichedPlan[0]?.messages?.length - 1; i>=0; i--){
              if(enrichedPlan[0]?.messages[i].author === "AI"){
                setSuggestedDescription(enrichedPlan[0]?.messages[i].content);
                break;
              }
            }
          }
        }
      
        showToast("Plan enriched successfully", "", "success");
      }
    } catch (error) {
      console.error("Error enriching plan:", error);
      showToast("Failed to enrich plan", "", "error");
    } finally {
      setIsEnrichingPlan(false);
    }
  };

  // Handle enrich plan with instructions modal open
  const handleEnrichPlanWithInstructionsModalOpen = () => {
    setEnrichInstruction("");
    onEnrichInstructionModalOpen();
  };

  // Handle enrich plan with instructions submit
  const handleEnrichPlanWithInstructionsSubmit = () => {
    handleEnrichPlan(enrichInstruction);
    onEnrichInstructionModalClose();
    setEnrichInstruction("");
  };

  // Handle enrich instruction modal close
  const handleEnrichInstructionModalClose = () => {
    onEnrichInstructionModalClose();
    setEnrichInstruction("");
  };

  const isDisabled = isLoading || isExecutingPlan || isFetchingPlan || isEnrichingPlan;

  return (
    <>
      <VStack spacing={4} align="stretch">
        {/* Header with Plan Name and Enrich Button */}
        <HStack spacing={3} justify="space-between">
          {/* Plan Name Display */}
          <Text fontSize="sm" fontWeight="semibold" color="gray.600">
            Plan: {currentPlanHeader?.name || "Unnamed Plan"}
          </Text>
          <Menu>
            <MenuButton
              as={Button}
              colorScheme="yellow"
              variant="outline"
              isDisabled={isDisabled}
              size="xs"
              leftIcon={<Icon as={FaMagic} boxSize={3} />}
              rightIcon={<Icon as={FaChevronDown} boxSize={3} />}
            >
              Enrich Plan
            </MenuButton>
            <MenuList>
              <MenuItem
                onClick={() => handleEnrichPlan("")}
                isDisabled={isDisabled}
              >
                <Icon as={FaMagic} boxSize={2} mr={2} />
                <Text fontSize="sm" fontWeight="medium" m={0}>
                  Without Instructions
                </Text>
              </MenuItem>
              <MenuItem
                onClick={handleEnrichPlanWithInstructionsModalOpen}
                isDisabled={isDisabled}
              >
                <Icon as={FaMagic} boxSize={2} mr={2} />
                <Text fontSize="sm" fontWeight="medium" m={0}>
                  With Instructions
                </Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>

        {/* Description Textarea */}
        <Textarea
          value={editingDescription}
          onChange={(e) => setEditingDescription(e.target.value)}
          onKeyDown={handleDescriptionKeyDown}
          placeholder="Enter plan description..."
          rows={6}
          resize="vertical"
          fontSize="sm"
          _focus={{
            borderColor: "purple.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
          }}
          isDisabled={isDisabled}
        />

        {/* Character Count */}
        <Text fontSize="xs" color="gray.500">
          {editingDescription.length}/1000 characters
        </Text>

        {/* Suggested Description */}
        {suggestedDescription && (
          <VStack spacing={2} align="stretch" p={3} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
            <HStack justify="space-between" align="flex-start">
              <Text fontSize="sm" fontWeight="semibold" color="blue.700" mb={1}>
                Suggested Description:
              </Text>
              <Button
                size="xs"
                colorScheme="blue"
                variant="solid"
                onClick={() => {
                  setEditingDescription(suggestedDescription);
                  setSuggestedDescription(""); // Clear suggestion after applying
                }}
                isDisabled={isDisabled}
              >
                Apply
              </Button>
            </HStack>
            <Text fontSize="sm" color="gray.700" bg="white" p={2} borderRadius="sm" borderWidth="1px" borderColor="gray.200">
              {suggestedDescription}
            </Text>
          </VStack>
        )}

        {/* Action Buttons */}
        <HStack spacing={3} justify="flex-end">
          <Button 
            variant="ghost" 
            onClick={() => setEditingDescription(currentPlanHeader?.description || "")}
            isDisabled={isDisabled}
          >
            Reset
          </Button>
          <Button 
            colorScheme="purple" 
            onClick={handleDescriptionSave}
            isDisabled={isDisabled || editingDescription.length >= 1000}
            isLoading={isLoading}
            loadingText="Saving..."
          >
            Save Description
          </Button>
        </HStack>
      </VStack>

      {/* Enrich Plan Instruction Modal */}
      <Modal 
        isOpen={isEnrichInstructionModalOpen} 
        onClose={handleEnrichInstructionModalClose}
        isCentered={false}
      >
        <ModalOverlay />
        <ModalContent
          maxHeight="60vh"
          maxWidth="40vw"
        >
          <ModalHeader>
            Enrich Plan with Instructions
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Provide specific instructions to guide the plan enrichment process:
              </Text>
              <Textarea
                value={enrichInstruction}
                onChange={(e) => setEnrichInstruction(e.target.value)}
                placeholder="Enter your instructions here..."
                rows={6}
                resize="vertical"
                fontSize="sm"
                _focus={{
                  borderColor: "purple.500",
                  boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                }}
              />
              <Text fontSize="xs" color="gray.500">
                {enrichInstruction.length}/1000 characters
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button 
              variant="ghost" 
              mr={3} 
              onClick={handleEnrichInstructionModalClose}
            >
              Cancel
            </Button>
            <Button 
              colorScheme="purple" 
              onClick={handleEnrichPlanWithInstructionsSubmit}
              isDisabled={!enrichInstruction.trim()}
              isLoading={isEnrichingPlan}
              loadingText="Enriching..."
            >
              Enrich Plan
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default PlanDescriptionComponent;
